import {
  BarChartOutlined,
  BulbOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
  OneToOneOutlined,
  PictureOutlined,
  ReadOutlined,
  SettingOutlined,
  ShoppingOutlined,
  ToolOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import type {
  RegisteredRouter,
  ValidateLinkOptions,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ToPath = ValidateLinkOptions<RegisteredRouter, unknown>['to'];

type UserMenuLeafItem = {
  key?: string;
  path: ToPath;
  label: string;
};

type UserMenuBase = {
  key: string;
  label: string;
  icon: ReactNode;
};

type UserMenuGroup = UserMenuBase & {
  children: UserMenuLeafItem[];
};

type UserMenuItem = UserMenuBase &
  (
    | {
        external?: false;
        path: ToPath;
      }
    | {
        external: true;
        path: string;
      }
  );

export type UserMenu = UserMenuGroup | UserMenuItem;

export const menuItems: UserMenu[] = [
  {
    icon: <ShoppingOutlined />,
    key: 'product-analysis',
    label: '爆款生成',
    path: '/product/analysis',
  },
  {
    icon: <OneToOneOutlined />,
    key: 'replication',
    label: '爆款复刻',
    path: '/video/replication',
  },
  {
    icon: <FileSearchOutlined />,
    key: 'analysis',
    label: '视频分析',
    path: '/video/analysis',
  },
  {
    icon: <VideoCameraOutlined />,
    label: '视频生成',
    key: 'videos',
    children: [
      {
        key: 'reference',
        path: '/video/reference-create',
        label: '多图视频生成',
      },
      {
        key: 'long_video',
        path: '/video/long/reference-create',
        label: '长视频生成',
      },
      {
        key: 'frame',
        path: '/video/create',
        label: '首尾帧视频',
      },
      {
        key: 'video-edit',
        path: '/video/edit',
        label: '视频编辑',
      },
      {
        key: 'translation',
        path: '/video/translation',
        label: '视频翻译',
      },
      {
        key: 'smart-erase',
        path: '/video/smart-erase',
        label: '智能擦除',
      },
      {
        key: 'quality-restoration',
        path: '/video/quality-restoration',
        label: '画质修复',
      },
    ],
  },
  {
    icon: <PictureOutlined />,
    label: '图片生成',
    key: 'images',
    children: [
      {
        key: 'images-generation',
        path: '/images',
        label: '图片生成',
      },
      {
        key: 'images-translation',
        path: '/images/translation',
        label: '图片翻译',
      },
    ],
  },
  {
    icon: <BulbOutlined />,
    label: '脚本模版库',
    key: 'script_template',
    path: '/script-template',
  },
  {
    icon: <ToolOutlined />,
    label: '技能包',
    key: 'skills',
    path: '/skills',
  },
  {
    icon: <FolderOpenOutlined />,
    label: '个人素材中心',
    key: 'assets',
    children: [
      {
        key: 'script',
        path: '/video/script',
        label: '历史脚本',
      },
      {
        key: 'video/history',
        path: '/video/history',
        label: '历史视频',
      },
      {
        key: 'tasks/image',
        path: '/tasks/image',
        label: '历史图片',
      },
    ],
  },
  {
    icon: <PictureOutlined />,
    label: '素材管理',
    key: 'aigc_assets',
    children: [
      {
        key: 'assets_list',
        path: '/avatars',
        label: '虚拟人物列表',
      },
    ],
  },
  {
    icon: <BarChartOutlined />,
    label: '我的数据统计',
    key: 'my-statistics',
    path: '/statistics',
  },
  {
    icon: <BarChartOutlined />,
    label: '数据统计',
    key: 'statistics',
    children: [
      {
        key: 'task',
        label: '任务统计',
        path: '/admin/statistics/task',
      },
      {
        key: 'usage',
        label: '信用点额度',
        path: '/admin/statistics/usage-limit',
      },
      {
        key: 'script',
        label: '历史脚本',
        path: '/admin/statistics/script',
      },
      {
        key: 'video',
        label: '历史视频',
        path: '/admin/statistics/video',
      },
    ],
  },
  {
    icon: <SettingOutlined />,
    label: '系统管理',
    key: 'system_management',
    children: [
      {
        key: 'announcements',
        label: '公告管理',
        path: '/admin/announcements',
      },
    ],
  },
  {
    icon: <ReadOutlined />,
    label: '帮助中心',
    key: 'help_center',
    external: true,
    path: getHelpCenterUrl(),
  },
];

function getHelpCenterUrl() {
  const url = new URL('https://auth-new.aigpt.com.co/login/oauth/authorize');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', 'e1cd89255b3b423e385c');
  url.searchParams.set('scope', 'openid+profile+email+offline_access');
  url.searchParams.set(
    'redirect_uri',
    'https://video-helpdesk.aigpt.com.co/api/method/frappe.integrations.oauth2_logins.custom/auth',
  );
  const state = {
    site: 'https://video-helpdesk.aigpt.com.co',
    token: uuidv4(),
    redirect_to:
      'https://video-helpdesk.aigpt.com.co/helpdesk/kb-public/mtte0j942v',
  };
  url.searchParams.set('state', btoa(JSON.stringify(state)));

  return url.toString();
}
